import React, { useEffect, useMemo, useState } from "react";
import Slider from "react-slick";
import {
  FaArrowLeft,
  FaArrowRight,
  FaExternalLinkAlt,
  FaHandshake,
} from "react-icons/fa";

import Reveal from "../../../components/Reveal/Reveal";
import { getAllPartenaires } from "../../../services/partenairesServices";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./PartenairesCarousel.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const isActivePartenaire = (partenaire) => {
  if (
    partenaire?.is_active === undefined ||
    partenaire?.is_active === null ||
    partenaire?.is_active === ""
  ) {
    return true;
  }

  return (
    partenaire.is_active === true ||
    partenaire.is_active === 1 ||
    partenaire.is_active === "1"
  );
};

const PartnerSkeleton = () => (
  <div className="px-3">
    <div className="h-56 animate-pulse rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="mx-auto mt-8 h-24 w-40 rounded-2xl bg-slate-200 dark:bg-slate-800" />
      <div className="mx-auto mt-7 h-4 w-32 rounded-full bg-slate-200 dark:bg-slate-800" />
    </div>
  </div>
);

const SliderArrow = ({ direction = "next", onClick }) => {
  const isNext = direction === "next";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`partner-arrow ${
        isNext ? "partner-arrow-next" : "partner-arrow-prev"
      }`}
      aria-label={isNext ? "Partenaire suivant" : "Partenaire précédent"}
    >
      {isNext ? <FaArrowRight /> : <FaArrowLeft />}
    </button>
  );
};

const PartnerCard = ({ partenaire }) => {
  const imageUrl = getImageUrl(partenaire?.photo_url);
  const linkUrl = partenaire?.link_url || partenaire?.url || "";
  const hasLink = Boolean(linkUrl);

  const content = (
    <article className="group relative h-full overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)] opacity-0 transition duration-500 group-hover:opacity-100" />

      <div className="relative flex h-32 items-center justify-center rounded-[1.5rem] border border-slate-100 bg-slate-50 p-5 transition duration-300 group-hover:border-orange-200 group-hover:bg-orange-50/60 dark:border-white/10 dark:bg-white/5 dark:group-hover:bg-white/10">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={partenaire?.nom || "Partenaire"}
            className="max-h-full max-w-full object-contain transition duration-500 group-hover:scale-105"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-primary/10 text-3xl text-primary">
            <FaHandshake />
          </div>
        )}
      </div>

      <div className="relative mt-5 text-center">
        <h3 className="line-clamp-2 font-barlow text-lg font-bold leading-snug text-slate-950 dark:text-white">
          {partenaire?.nom || "Partenaire"}
        </h3>

        {hasLink && (
          <p className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500 transition group-hover:bg-primary group-hover:text-white dark:bg-white/10 dark:text-slate-300">
            Site partenaire
            <FaExternalLinkAlt className="text-[10px]" />
          </p>
        )}
      </div>
    </article>
  );

  if (!hasLink) {
    return <div className="h-full px-3">{content}</div>;
  }

  return (
    <a
      href={linkUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block h-full px-3 focus:outline-none"
      aria-label={`Ouvrir le site du partenaire ${partenaire?.nom || ""}`}
    >
      {content}
    </a>
  );
};

export default function PartenairesCarousel() {
  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchPartenaires = async () => {
      try {
        const allPartenaires = await getAllPartenaires();

        if (!mounted) return;

        const activePartenaires = (Array.isArray(allPartenaires)
          ? allPartenaires
          : []
        ).filter(isActivePartenaire);

        setPartenaires(activePartenaires);
      } catch (error) {
        console.error("Erreur lors de la récupération des partenaires :", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchPartenaires();

    return () => {
      mounted = false;
    };
  }, []);

  const sliderSettings = useMemo(() => {
    const count = partenaires.length;
    const shouldAutoplay = count > 1;

    return {
      infinite: shouldAutoplay,
      slidesToShow: Math.min(4, Math.max(count, 1)),
      slidesToScroll: 1,
      autoplay: shouldAutoplay,
      autoplaySpeed: 2600,
      speed: 750,
      cssEase: "ease",
      pauseOnHover: true,
      arrows: count > 1,
      dots: count > 1,
      swipeToSlide: true,
      nextArrow: <SliderArrow direction="next" />,
      prevArrow: <SliderArrow direction="prev" />,
      responsive: [
        {
          breakpoint: 1280,
          settings: {
            slidesToShow: Math.min(3, Math.max(count, 1)),
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: Math.min(2, Math.max(count, 1)),
            arrows: false,
          },
        },
        {
          breakpoint: 520,
          settings: {
            slidesToShow: 1,
            arrows: false,
          },
        },
      ],
    };
  }, [partenaires.length]);

  if (!loading && partenaires.length === 0) {
    return null;
  }

  return (
    <section className="relative overflow-hidden bg-white py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-24">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.10),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.08),transparent_32%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.12)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.12)_1px,transparent_1px)] bg-[size:42px_42px] opacity-30 dark:opacity-15" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto mb-12 max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaHandshake />
            Ils nous accompagnent
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Nos{" "}
            <span className="text-primary">
              partenaires
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Merci aux structures, entreprises et institutions qui soutiennent la
            vie du club et contribuent à son développement.
          </p>
        </Reveal>

        <Reveal variant="zoom">
          <div className="rounded-[2.5rem] border border-slate-200 bg-white/80 px-4 py-7 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-white/5 sm:px-7">
            {loading ? (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <PartnerSkeleton key={`partner-skeleton-${index}`} />
                ))}
              </div>
            ) : (
              <Slider {...sliderSettings} className="partners-slider">
                {partenaires.map((partenaire) => (
                  <PartnerCard key={partenaire.id} partenaire={partenaire} />
                ))}
              </Slider>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}